import { useState } from 'react';
import { api } from './api.js';
import { openInvoice } from './telegram.js';
import { t } from './i18n.js';
import { useToast } from '../stores/toast.js';
import { useAuth } from '../stores/auth.js';

export type TopupOutcome = 'paid' | 'cancelled' | 'failed' | 'unavailable';

interface UseTopupResult {
  /** True while invoice is being created or while the Telegram sheet is open. */
  inFlight: boolean;
  /**
   * Open a Stars invoice for `amount`. On success, refreshes /me so the new balance lands
   * in the auth store. The promise resolves once the Telegram sheet closes (or fails).
   * `successToast` defaults to "Зачислено" — pass a custom string when integrating into a
   * specific flow (e.g. lobby's "повторяем поиск").
   */
  topup: (amount: number, successToast?: string) => Promise<TopupOutcome>;
}

export function useTopup(): UseTopupResult {
  const [inFlight, setInFlight] = useState(false);
  const pushToast = useToast((s) => s.push);
  const refresh = useAuth((s) => s.refresh);

  async function topup(amount: number, successToast?: string): Promise<TopupOutcome> {
    if (inFlight) return 'failed';
    setInFlight(true);
    try {
      const { invoiceUrl } = await api.topupInvoice(amount);
      const status = await openInvoice(invoiceUrl);
      if (status === 'paid') {
        pushToast(successToast ?? t.paymentCreditedSimple, 'success');
        await refresh();
        return 'paid';
      }
      if (status === 'cancelled') {
        pushToast(t.paymentCancelled, 'info');
        return 'cancelled';
      }
      if (status === 'unavailable') {
        pushToast(t.paymentUnavailable, 'error');
        return 'unavailable';
      }
      pushToast(t.paymentFailed(status), 'error');
      return 'failed';
    } catch (err) {
      pushToast(err instanceof Error ? err.message : 'Не удалось открыть оплату', 'error');
      return 'failed';
    } finally {
      setInFlight(false);
    }
  }

  return { inFlight, topup };
}
